import { inject, Injectable } from '@angular/core';
import { UuidExtraInfoLoggingService } from '@lx/quote-tool/shared/services';
import { QuoteInput, StudentDetails } from '@lx/quote-tool/shared/types';
import { obfuscateText } from '@lx/quote-tool/shared/utils/functions';
import { CityExtended, Country, CourseType } from '@lx/shared/data-access/types';
import { areArraysEqual, getDateStringFromDate, isDeepEqual } from '@lx/shared/utils';
import { ComponentStore } from '@ngrx/component-store';
import { debounceTime, distinctUntilChanged, skip } from 'rxjs';
import { tap } from 'rxjs/operators';
import { defaultPriceRequestExperimentState, PriceRequestFormState } from '../state';

@Injectable({ providedIn: 'root' })
export class PriceRequestFormStore extends ComponentStore<PriceRequestFormState> {
  constructor() {
    super(defaultPriceRequestExperimentState);
  }

  private readonly uuidExtraLoggingService = inject(UuidExtraInfoLoggingService);

  // --------------------------Selectors--------------------------
  readonly state$ = this.select((s) => s);
  readonly quoteInput$ = this.select((s) => s.quoteInput);
  readonly quoteLanguage$ = this.select((s) => s.quoteInput?.languageCode);
  readonly quoteCourseType$ = this.select((s) => s.courseType);
  readonly studentDetails$ = this.select((s) => s.studentDetails);
  readonly specialRequests$ = this.select((s) => s.studentDetails?.specialRequests);
  readonly destinationCities$ = this.select((s) => s.destinationCities);
  readonly courseTypes$ = this.select((s) => s.courseTypes);

  readonly selectedLanguage$ = this.select(({ quoteInput }) => quoteInput?.languageCode);
  readonly selectedCourseType$ = this.select(({ quoteInput }) => quoteInput?.courseType);

  readonly destinationCountriesAndCourseType$ = this.select(
    ({ destinationCountries, quoteInput }) => ({
      destinationCountries,
      courseType: quoteInput?.courseType,
    }),
    {
      debounce: true,
      equal: (prev, curr) => areArraysEqual(prev.destinationCountries, curr.destinationCountries) && prev.courseType === curr?.courseType,
    },
  );

  readonly selectedCourseTypeAndSelectedLanguageAndDestinationCity$ = this.select(
    ({ quoteInput }) => ({
      courseType: quoteInput?.courseType,
      languageCode: quoteInput?.languageCode,
      destinationCityId: quoteInput?.destinationCity?.code,
    }),
    {
      debounce: true,
      equal: (prev, curr) =>
        prev.courseType === curr?.courseType && prev.languageCode === curr?.languageCode && prev.destinationCityId === curr?.destinationCityId,
    },
  );

  readonly selectionForDataLayer$ = this.select(
    ({ quoteInput, studentDetails, courseType }) => ({
      language: quoteInput?.languageCode,
      courseType: courseType?.name,
      country: quoteInput?.country,
      city: quoteInput?.destinationCity,
      duration: quoteInput?.tuitionWeeks,
      startDate: quoteInput?.startDate,
      accommodationType: quoteInput?.accommodationType,
      specialRequests: studentDetails?.specialRequests,
      studentName: studentDetails?.firstName,
      studentLastName: studentDetails?.lastName,
      email: studentDetails?.email,
      phoneNumber: studentDetails?.phoneNumber,
      countryOfResidence: studentDetails?.countryOfResidence,
      postCode: studentDetails?.postcode,
      dateOfBirth: studentDetails?.dateOfBirth,
      consentMarketing: studentDetails?.consentMarketing,
    }),
    { debounce: true },
  );

  // --------------------------Updaters---------------------------
  readonly updateQuoteInput = this.updater((state, quoteInput: QuoteInput) => {
    const newQuoteInput = { ...state.quoteInput, ...quoteInput };
    return {
      ...state,
      quoteInput: newQuoteInput,
    };
  });

  readonly updateCourseType = this.updater((state, courseType: CourseType) => ({
    ...state,
    courseType,
  }));

  readonly updateStudentDetails = this.updater((state, studentDetails: StudentDetails) => {
    const newStudentDetails = { ...state.studentDetails, ...studentDetails };
    return {
      ...state,
      studentDetails: newStudentDetails,
    };
  });

  readonly updateConsentMarketing = this.updater((state, consentMarketing: boolean) => {
    return {
      ...state,
      studentDetails: {
        ...state.studentDetails,
        consentMarketing,
      },
    };
  });

  readonly updateDestinationCountries = this.updater((state, destinationCountries: Country[]) => ({
    ...state,
    destinationCountries,
  }));
  readonly updateDestinationCities = this.updater((state, destinationCities: CityExtended[]) => ({
    ...state,
    destinationCities,
  }));

  readonly updateCourseTypes = this.updater((state, courseTypes: string[]) => ({
    ...state,
    courseTypes,
  }));

  initUuidExtraLogging(): void {
    this.uuidExtraLoggingService.logMessage(`initialized PriceRequestFormStore`);
    this.logStudentDetailsChanges(this.studentDetails$);
    this.logQuoteInputChanges(this.quoteInput$);
  }

  // --------------------------Effects---------------------------
  readonly logQuoteInputChanges = this.effect<QuoteInput | undefined>((quoteInput$) => {
    return quoteInput$.pipe(
      skip(1),
      distinctUntilChanged((firstQuote, secondQuote) => isDeepEqual(firstQuote, secondQuote)),
      tap((quoteInput: QuoteInput | undefined) => {
        const parsedQuoteInput = JSON.stringify({
          ...quoteInput,
          country: quoteInput?.country?.name ?? quoteInput?.country,
          destinationCity: quoteInput?.destinationCity?.code ?? quoteInput?.destinationCity,
        });
        this.uuidExtraLoggingService.logMessage(`${PriceRequestFormStore.name} ${parsedQuoteInput}`);
      }),
    );
  });

  readonly logStudentDetailsChanges = this.effect<StudentDetails | undefined>((studentDetails$) => {
    return studentDetails$.pipe(
      skip(1),
      distinctUntilChanged((firstDetails, secondDetails) => isDeepEqual(firstDetails, secondDetails)),
      debounceTime(500), // Debounce to avoid logging too many events in a short time
      tap((studentDetails: StudentDetails | undefined) => {
        const fieldsToObfuscate = [
          { key: 'firstName', length: 3 },
          { key: 'lastName', length: 3 },
          { key: 'email', length: 3, email: true },
          { key: 'phoneNumber', length: 7 },
          { key: 'postcode', length: 3 },
          { key: 'dateOfBirth', length: 3 },
        ];
        const obfuscatedDetails = fieldsToObfuscate.reduce(
          (acc, { key, length, email }) => {
            let value = studentDetails?.[key];
            if (key === 'dateOfBirth') {
              value = getDateStringFromDate(value);
            }
            acc[key] = obfuscateText(value, length, email);
            return acc;
          },
          { ...studentDetails },
        );
        const parsedStudentDetails = JSON.stringify(obfuscatedDetails);
        this.uuidExtraLoggingService.logMessage(`${PriceRequestFormStore.name} ${parsedStudentDetails}`);
      }),
    );
  });
}
